import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import Login from "@/pages/Login";
import Layout from "@/components/app/Layout";
import PartnerDashboard from "@/pages/partner/Dashboard";
import Catalog from "@/pages/partner/Catalog";
import CourseDetail from "@/pages/partner/CourseDetail";
import Cart from "@/pages/partner/Cart";
import PartnerOrders from "@/pages/partner/Orders";
import OrderDetail from "@/pages/partner/OrderDetail";
import PartnerCalendar from "@/pages/partner/Calendar";
import PartnerEnrollments from "@/pages/partner/Enrollments";
import Support from "@/pages/Support";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminCourses from "@/pages/admin/Courses";
import AdminEditions from "@/pages/admin/Editions";
import AdminPartners from "@/pages/admin/Partners";
import AdminOrders from "@/pages/admin/Orders";
import AdminEnrollments from "@/pages/admin/Enrollments";
import AdminLogs from "@/pages/admin/Logs";
import PartnerDocuments from "@/pages/partner/Documents";
import AdminDocuments from "@/pages/admin/Documents";
import PartnerRecharge from "@/pages/partner/Recharge";
import AdminPackages from "@/pages/admin/Packages";
import AdminPromoCodes from "@/pages/admin/PromoCodes";
import AdminRecharges from "@/pages/admin/Recharges";
import AdminClients from "@/pages/admin/Clients";
import AdminClientReports from "@/pages/admin/ClientReports";
import ClienteDashboard from "@/pages/cliente/Dashboard";
import ClienteNewEnrollment from "@/pages/cliente/NewEnrollment";
import ClienteEnrollments from "@/pages/cliente/Enrollments";
import ClienteProfile from "@/pages/cliente/Profile";
import { homeFor } from "@/lib/roles";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import RequirePerm from "@/components/app/RequirePerm";
import AdminEmployees from "@/pages/admin/Employees";
import EmployeeForm from "@/pages/admin/EmployeeForm";
import StaffRoles from "@/pages/admin/StaffRoles";
import ChangePasswordRequired from "@/pages/ChangePasswordRequired";
import ClienteCatalog from "@/pages/cliente/Catalog";
import ClienteCalendar from "@/pages/cliente/Calendar";
import ClienteCourseDetail from "@/pages/cliente/CourseDetail";
import ActivationRequests from "@/pages/admin/ActivationRequests";

const P = (p, el) => <RequirePerm p={p}>{el}</RequirePerm>;

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="p-10 text-slate-500">Caricamento…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={homeFor(user.role)} replace />;
  if (user.must_change_password) return <ChangePasswordRequired />;
  return children;
}

function Root() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-slate-500">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/password-dimenticata" element={<ForgotPassword />} />
            <Route path="/reimposta-password" element={<ResetPassword />} />

            <Route path="/partner" element={<Protected role="partner"><Layout /></Protected>}>
              <Route index element={<PartnerDashboard />} />
              <Route path="catalogo" element={<Catalog />} />
              <Route path="corsi/:id" element={<CourseDetail />} />
              <Route path="calendario" element={<PartnerCalendar />} />
              <Route path="carrello" element={<Cart />} />
              <Route path="ordini" element={<PartnerOrders />} />
              <Route path="ordini/:id" element={<OrderDetail />} />
              <Route path="ricarica" element={<PartnerRecharge />} />
              <Route path="iscrizioni" element={<PartnerEnrollments />} />
              <Route path="supporto" element={<Support />} />
              <Route path="documenti" element={<PartnerDocuments />} />
            </Route>

            <Route path="/admin" element={<Protected role="admin"><Layout /></Protected>}>
              <Route index element={P("dashboard", <AdminDashboard />)} />
              <Route path="corsi" element={P("courses", <AdminCourses />)} />
              <Route path="edizioni" element={P("editions", <AdminEditions />)} />
              <Route path="partner" element={P("partners", <AdminPartners />)} />
              <Route path="ordini" element={P("orders", <AdminOrders />)} />
              <Route path="ordini/:id" element={P("orders", <OrderDetail />)} />
              <Route path="pacchetti" element={P("recharges", <AdminPackages />)} />
              <Route path="codici-promo" element={P("recharges", <AdminPromoCodes />)} />
              <Route path="ricariche" element={P("recharges", <AdminRecharges />)} />
              <Route path="clienti" element={P("clients", <AdminClients />)} />
              <Route path="clienti-iscrizioni" element={P("enrollments_clients", <AdminClientReports mode="enrollments" />)} />
              <Route path="clienti-report" element={P("client_reports", <AdminClientReports mode="reports" />)} />
              <Route path="richieste-attivazione" element={P("activation_requests", <ActivationRequests />)} />
              <Route path="iscrizioni" element={P("enrollments_partner", <AdminEnrollments />)} />
              <Route path="supporto" element={P("support", <Support />)} />
              <Route path="documenti" element={P("documents", <AdminDocuments />)} />
              <Route path="log" element={P("system_config", <AdminLogs />)} />
              <Route path="dipendenti" element={P("employees", <AdminEmployees />)} />
              <Route path="dipendenti/nuovo" element={P("employees", <EmployeeForm />)} />
              <Route path="dipendenti/ruoli" element={P("employees", <StaffRoles />)} />
              <Route path="dipendenti/:id" element={P("employees", <EmployeeForm />)} />
            </Route>

            <Route path="/cliente" element={<Protected role="cliente"><Layout /></Protected>}>
              <Route index element={<ClienteDashboard />} />
              <Route path="catalogo" element={<ClienteCatalog />} />
              <Route path="corsi/:id" element={<ClienteCourseDetail />} />
              <Route path="calendario" element={<ClienteCalendar />} />
              <Route path="iscrizioni" element={<ClienteNewEnrollment />} />
              <Route path="storico" element={<ClienteEnrollments />} />
              <Route path="profilo" element={<ClienteProfile />} />
            </Route>

            <Route path="/" element={<Root />} />
            <Route path="*" element={<Root />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
